import { uploadImage } from './uploadImageService';
import { getReq, putReq, postReq, patchReq, deleteReq, webDownloadFile, Pagination } from './api';
import { getDeviceInfo } from '../utils/deviceInfo';
import { FormattedFormOfficialData } from '../utils/officialFormHelpers';
import { Agency, FeatureFlag, NewOfficialWithToken } from '../utils/types';

// Employment types (designations) are served by the /info endpoint. See useInfo
export type EmploymentType = string;

export interface OfficialSummary {
  id: number;
  first_name: string;
  last_name: string;
  img_url: string | null;
}

export interface OfficialCredential {
  id: number;
  official_credential_id: string;
  client_provided: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  agency_official_id: number;
  credential_type: string;
}

export interface CredentialInput {
  credential_type: string;
  status?: string;
}

export interface CredentialInput {
  credential_type: string;
  status?: string;
}

export interface AgencyGroup {
  id: number;
  agency_id: number;
  default: boolean;
  name: string;
}

interface AgencyOfficialRole {
  id: number;
  name: string;
}

interface AgencyOfficialType {
  name: string;
  id: number;
}

interface AuthAction {
  id: number;
  agency_official_id: number;
  device_id: string;
  auth_action: string;
  created_at: string;
  updated_at: string;
}

export interface OfficialData {
  id: number;
  first_name: string;
  last_name: string;
  official_id: number;
  agency_id: number;
  invite_status: string;
  title: string;
  email: string;
  agency_official_type: AgencyOfficialType | null;
  img_url?: string;
  id_card_front_url?: string | null;
  id_card_back_url?: string | null;
  agency_official_type_id: number | null;
  employment_type: EmploymentType;
  rank: string;
  role: AgencyOfficialRole;
  verified: boolean;
  badge_number: string;
  armed_permission: boolean;
  status: string;
  notes?: string | null;
  phone_number?: string | null;
  agency: Agency;
  feature_flags: FeatureFlag[];
  universal_feature_flags: FeatureFlag[];
  groups: AgencyGroup[] | [];
  post_verifications: Verification[] | [];
  agency_verifications: Verification[] | [];
  official_credentials: OfficialCredential[];
  email_verified: boolean;
  pin?: string;
  name?: string;
}

export interface AgencyOffifialExtendedSummary {
  id: number;
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  img_url?: string;
  employment_type: EmploymentType;
  rank: string;
  role: AgencyOfficialRole;
  badge_number: string;
  armed_permission: boolean;
  phone_number?: string | null;
  agency: {
    name: string
  }
  verified: boolean;
  status: 'active' | 'suspended';
};

export interface Verification {
  id: number;
  pending_at: string;
  completed_at: string | null;
  status: 'verified' | 'unverified' | 'pending';
  verifying_agency: Agency;
  requesting_agency: Agency;
  verifying_agency_official: OfficialData | null;
  requesting_agency_official: OfficialData | null;
  agency_official?: OfficialData;
}

// lots of properties we probably won't need, so I'm just including a few and we can always add more later.
export interface PartialCloudinaryImageAsset {
  existing: boolean;
  url: string;
  bytes: number;
  access_mode: string;
  public_id: string;
}

export interface VerificationUpdate {
  verification: { status: string };
  id?: number;
}

export interface AgencyOfficialSearchResult {
  id: number;
  first_name: string,
  last_name: string,
  agency_name: string;
}

export const fetchAgencyOfficials = async (params: {
  accessToken: string;
  limit?: number;
  offset?: number;
  agencyId?: string | number;
  query?: string;
  inviteStatuses?: string[];
  statuses?: string[];
  armedPermissions?: string[];
  verified?: string[];
  hasImage?: string[];
}): Promise<{ data?: OfficialData[]; pagination?: Pagination; error?: any }> => {
  const { accessToken, agencyId, limit = 100, offset = 0, query, inviteStatuses, statuses, armedPermissions, verified, hasImage } = params;

  if (!agencyId) {
    return { error: 'Agency id is required' };
  }

  const searchParams = new URLSearchParams();
  searchParams.append('limit', String(limit));
  searchParams.append('offset', String(offset));
  if (query?.length) searchParams.append('query', query);
  if (inviteStatuses?.length) searchParams.append('invite_status', inviteStatuses.join(','));
  if (statuses?.length) searchParams.append('status', statuses.join(','));
  if (armedPermissions?.length) searchParams.append('armed_permission', armedPermissions.join(','));
  if (verified?.length) searchParams.append('verified', verified.join(','));
  if (hasImage?.length) searchParams.append('has_image', hasImage.join(','));

  return await getReq<OfficialData[]>(
    `/agencies/${agencyId}/agency_officials/search?${searchParams.toString()}`,
    accessToken,
    'Failed to fetch officials.'
  );
};

/** Downloads a CSV of all the agency's officials, with times in the browser's time zone. */
export const downloadAgencyOfficialsReport = async (accessToken: string, agencyId: number | string) => {
  const params = new URLSearchParams();
  params.append('report_tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
  return webDownloadFile(`/agencies/${agencyId}/agency_officials_report?${params}`, accessToken);
};

export const getOfficial = async (
  accessToken: string,
  officialId: number | string
): Promise<{ data?: OfficialData; error?: any }> => {
  return getReq<OfficialData>(
    `/agency_officials/${officialId}`,
    accessToken,
    'Failed to fetch official.'
  );
};

export const getCurrentOfficial = async (
  accessToken
): Promise<{ data?: OfficialData; error?: any }> => {
  return getReq<OfficialData>(
    '/current_agency_official',
    accessToken,
    'Failed to get current official.'
  );
};

export const updateOfficial = async (
  accessToken: string,
  officialUpdates: Partial<OfficialData> | Partial<FormattedFormOfficialData>
): Promise<{ data?: OfficialData; error?: string }> => {
  return putReq<OfficialData>(
    `/agency_officials/${officialUpdates.id}`,
    accessToken,
    officialUpdates,
    'Failed to update official.'
  );
};

export const addAgencyOfficial = async (
  accessToken: string,
  agencyId: string | number,
  data: FormattedFormOfficialData
): Promise<{ data?: OfficialData; error?: string }> => {
  return postReq<OfficialData>(
    `/agencies/${agencyId}/agency_officials`,
    accessToken,
    data,
    'Failed to update official.'
  );
};

export const publicAddAgencyOfficial = async (
  data: NewOfficialWithToken
): Promise<{ error?: string; status?: number }> => {
  return postReq<{ status: number }>(
    `/registrations`,
    undefined,
    data,
    'Failed to register official.'
  );
};

export const addCredential = async (
  accessToken: string,
  officialId: number,
  credential: CredentialInput
): Promise<{ data?: OfficialCredential; error?: string }> => {
  return postReq<OfficialCredential>(
    `/agency_officials/${officialId}/credentials`,
    accessToken,
    credential,
    'Failed to add credential.'
  );
};

export const updateCredential = async (
  accessToken: string,
  credential: Partial<OfficialCredential> & {
    official_credential_id: OfficialCredential['official_credential_id'];
  }
): Promise<{ data?: OfficialCredential; error?: string }> => {
  return putReq<OfficialCredential>(
    `/credentials/${credential.official_credential_id}`,
    accessToken,
    credential,
    'Failed to update credential.'
  );
};

export const deleteCredential = async (
  accessToken: string,
  credentialId: string
): Promise<{ data?: { message: string }; error?: string }> => {
  const { data, error } = await updateCredential(accessToken, {
    official_credential_id: credentialId,
    status: 'deactivated'
  });
  if (error) {
    return { error };
  }
  if (!data) {
    return { error: 'Failed to delete credential.' };
  }
  return { data: { message: 'Credential deleted.' } };
};

export const updateOfficialImage = async (
  accessToken: string,
  officialId: number,
  agencyId: number | string,
  imageFile: File
): Promise<{ data?: OfficialData; error?: any }> => {
  const folder = `${agencyId}/official_images`;
  const uploadPreset = 'official_img';
  const { data: imageData } = await uploadImage({
    accessToken,
    folder,
    uploadPreset,
    file: imageFile
  });

  if (imageData?.secure_url) {
    return await updateOfficial(accessToken, {
      id: officialId,
      img_url: imageData.secure_url
    });
  }

  return { error: 'Failed to update official image' };
};

export type IdCardSide = 'front' | 'back';

export interface IdCardFileIds {
  front_file_id?: string;
  back_file_id?: string;
}

// Attaches already-uploaded files (see createFile) to the official's law enforcement ID card.
// Only the sides passed in are changed, so a single side can be replaced on its own.
export const updateOfficialIdCards = async (
  accessToken: string,
  officialId: number | string,
  idCard: IdCardFileIds
): Promise<{ data?: OfficialData; error?: string }> =>
  patchReq<OfficialData>(
    `/agency_officials/${officialId}/id_cards`,
    accessToken,
    { id_card: idCard },
    'Failed to update ID card.'
  );

// Removes one side of the official's law enforcement ID card. The endpoint responds 204 with no
// body, so a successful call resolves without data - check `error` rather than `data`.
export const removeOfficialIdCard = async (
  accessToken: string,
  officialId: number | string,
  side: IdCardSide
): Promise<{ error?: string; status?: number }> =>
  deleteReq(
    `/agency_officials/${officialId}/id_cards/${side}`,
    accessToken,
    'Failed to remove ID card.'
  );

export const sendEmailInvites = async (
  accessToken: string,
  ids: number[]
): Promise<{ data?: number[]; error?: string }> => {
  return postReq<number[]>(
    `/invites`,
    accessToken,
    { agency_officials: ids },
    'Failed to send email invites.'
  );
};

export const createAuthAction = async (
  accessToken: string,
  officialId: number,
  auth_action: string
): Promise<{ data?: AuthAction; error?: string }> => {
  return postReq<AuthAction>(
    `/agency_officials/${officialId}/auth_actions`,
    accessToken,
    { device_id: getDeviceInfo(), auth_action: auth_action },
    'Failed to create auth action.'
  );
};

export const getAgencyOfficials = async (params: {
  accessToken: string;
  limit?: number;
  offset?: number;
  agencyId: number | string | 'all';
  employmentType?: string
}): Promise<{ data?: OfficialData[]; pagination?: Pagination; error?: string }> => {
  const { accessToken, limit = 1000, offset = 0, agencyId, employmentType } = params;
  let queryString = `/agencies/${agencyId}/agency_officials?limit=${limit}&offset=${offset}`

  if (employmentType) {
    queryString += `&employment_type=${employmentType}`
  }

  return getReq<OfficialData[]>(queryString, accessToken);
};

export const searchAgencyOfficials = async ({
  accessToken,
  search,
  limit = 10,
}: {
  accessToken: string;
  search: string;
  limit?: number;
}): Promise<{ data?: AgencyOfficialSearchResult[]; error?: string }> => {
  const params = new URLSearchParams();
  params.append('search', search);
  params.append('limit', String(limit));
  return getReq<AgencyOfficialSearchResult[]>(
    `/agency_officials/search?${params.toString()}`,
    accessToken,
    'Failed to search officials.'
  );
};

export const getAllAgencyOfficials = async (params: {
  accessToken: string;
  limit?: number;
  offset?: number;
  agencyId: number | string | 'all';
}): Promise<{ data?: OfficialData[]; pagination?: Pagination; error?: string }> => {
  const { accessToken, limit = 1000, offset = 0, agencyId } = params;

  const agencyQuery = agencyId === 'all' ? '' : `agency_id=${agencyId}&`;
  return getReq<OfficialData[]>(
    `/agency_officials?${agencyQuery}limit=${limit}&offset=${offset}`,
    accessToken
  );
};

export const updateVerification = async (
  accessToken: string,
  updatedData: VerificationUpdate
): Promise<{ data?: Verification; error?: string }> => {
  const verificationId = updatedData.id;
  delete updatedData.id;
  return patchReq<Verification>(
    `/verifications/${verificationId}`,
    accessToken,
    updatedData,
    'Failed to POST verification update.'
  );
};

export const bulkVerifyOfficials = async (
  accessToken: string,
  agencyId: number | string,
  officialIds: (string | number)[]
): Promise<{ data?: string[]; error?: string; status?: number; errors?: string[] }> => {
  return patchReq<string[]>(
    `/agencies/${agencyId}/bulk_verifications`,
    accessToken,
    { agency_official_ids: officialIds },
    'Failed to bulk verify officials.'
  );
};

export const bulkUploadOfficials = async (
  accessToken: string,
  agencyId: string | number,
  file: File
): Promise<{ data?: any[]; error?: string; errors?: string[] }> => {
  const formData = new FormData();
  formData.append('file', file);
  return postReq<any[]>(
    `/agencies/${agencyId}/bulk_upload_officials`,
    accessToken,
    formData,
    'Failed to upload officials.',
    'multipart/form-data',
    false
  );
};

export const sharePin = async (
  accessToken: string,
  officialId: number,
  emails: string[]
): Promise<{ data?: string[]; error?: string }> => {
  return postReq<any[]>(
    `/agency_officials/${officialId}/pin/share`,
    accessToken,
    { emails },
    'Failed to share PIN.'
  );
};

export const regeneratePin = async (
  accessToken: string,
  officialId: number
): Promise<{ data?: OfficialData; error?: string }> => {
  return patchReq<OfficialData>(
    `/agency_officials/${officialId}/pin/regenerate`,
    accessToken,
    'Failed to regenerate PIN.'
  );
};

/** Downloads a CSV of every user of every agency. Super Admin only. */
export const downloadUserListReport = async (accessToken: string) => {
  const params = new URLSearchParams();
  params.append('report_tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
  return webDownloadFile(`/agency_officials_report?${params}`, accessToken);
};
